import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { flatten, values } from "lodash";
import { GatewayChatProps } from "@/shared/widgets/GatewayChat";
import { getGptFlowKey, GPTFlowKey } from "@/shared/constants/gpt-flow-types";
import { Person } from "@/features/people/graphql/generated";
import { CustomTagsData } from "@/shared/widgets/MarkdownText/_components/MarkdownComponents";
import { WisgptContextTokenQueryVariables } from "@/shared/services/api/graphql/generated";

/**
 * Profile context types that determine the AI chat configuration
 */
export type ProfileContextType =
  | "personal_profile"
  | "team_profile"
  | "team_member_profile"
  | "person_profile"
  | "candidate_profile";

/**
 * Configuration for building profile layout props
 */
export interface ProfileLayoutConfig {
  /** The type of profile context */
  contextType: ProfileContextType;
  /** The primary entity (person/team) for the profile */
  entity: {
    id?: number | null;
    name?: string | null;
    first_name?: string | null;
  };
  /** Dimensions data from useDimensionsWithScores */
  dimensionsData: Record<string, any>;
  /** Survey configuration */
  surveyConfig?: any;
  /** People to include in tags data */
  people: (Person | any)[];
  /** Attachments for tags data */
  attachments?: any[];
  /** Loading states to determine isReady */
  loadingStates: boolean[];
  /** Additional scope properties */
  additionalScope?: Partial<WisgptContextTokenQueryVariables>;
  /** Custom example prompts (optional - will use defaults based on context) */
  customExamplePrompts?: string[];
}

/**
 * Return type for useProfileLayoutProps hook
 */
export interface ProfileLayoutPropsResult {
  /** Props to spread on ProfileLayout component */
  layoutProps: Omit<GatewayChatProps, "chatTitle">;
  /** Tags data for summary sections and other components */
  tagsData: CustomTagsData;
  /** Scope for AI chat and summary generation */
  scope: WisgptContextTokenQueryVariables;
  /** Whether the profile is ready to display */
  isReady: boolean;
  /** Context-specific loading message */
  loadingMessage: string;
}

/**
 * Flow ID mapping for different profile contexts
 */
const FLOW_ID_MAP: Record<ProfileContextType, string> = {
  personal_profile: "personal_profile_chat",
  team_profile: "team_profile_chat",
  team_member_profile: "team_member_profile_chat",
  person_profile: "organization_member_profile_chat",
  candidate_profile: "candidate_profile_chat",
};

/**
 * Loading message i18n keys for different profile contexts
 */
const LOADING_MESSAGE_KEYS: Record<ProfileContextType, string> = {
  personal_profile: "common.loading_profile",
  team_profile: "common.loading_team_profile",
  team_member_profile: "common.loading_profile",
  person_profile: "common.loading_profile",
  candidate_profile: "common.loading_profile",
};

/**
 * AI greeting message translation keys for different profile contexts
 */
const GREETING_KEYS: Record<ProfileContextType, string> = {
  personal_profile: "profile_layout.ai_greeting_personal",
  team_profile: "profile_layout.ai_greeting_team",
  team_member_profile: "profile_layout.ai_greeting_team_member",
  person_profile: "profile_layout.ai_greeting_person",
  candidate_profile: "profile_layout.ai_greeting_candidate",
};

/**
 * Build tags data for AI chat and markdown components
 */
export function buildTagsData(
  dimensionsData: Record<string, any>,
  people: (Person | any)[],
  attachments: any[] = [],
  surveyConfig?: any
): CustomTagsData {
  return {
    dimensions: flatten(values(dimensionsData)),
    people,
    attachments,
    ...(surveyConfig && { survey_config: surveyConfig }),
  };
}

/**
 * Build focused context for AI chat
 */
export function buildFocusedContext(
  entityId: number | string | null | undefined,
  greetingMessage: string
): GatewayChatProps["focusedContext"] {
  return {
    id: entityId?.toString() || "default",
    message: {
      role: "assistant",
      content: greetingMessage,
      disableCopy: true,
    },
  };
}

/**
 * Get default example prompts based on profile context
 */
function getDefaultExamplePrompts(
  contextType: ProfileContextType,
  entityName: string,
  t: (key: string, params?: Record<string, string>) => string
): string[] {
  const name = entityName || "them";

  switch (contextType) {
    case "personal_profile":
      return [
        t("profile.example_prompts.career_paths"),
        t("profile.example_prompts.recommend_books"),
        t("profile.example_prompts.weekly_calendar"),
        t("profile.example_prompts.explain_results"),
        t("profile.example_prompts.personality_animal"),
      ];

    case "team_profile":
      return [
        t("profile_layout.prompt_team_meetings"),
        t("profile_layout.prompt_team_avoid"),
        t("profile_layout.prompt_team_tasks"),
        t("profile_layout.prompt_team_productive"),
        t("profile_layout.prompt_team_avengers"),
      ];

    case "team_member_profile":
      return [
        t("profile_layout.prompt_member_avoid", { name }),
        t("profile_layout.prompt_member_support", { name }),
        t("profile_layout.prompt_member_conflicts", { name }),
        t("profile_layout.prompt_member_skill", { name }),
        t("profile_layout.prompt_member_pooh", { name }),
      ];

    case "person_profile":
      return [
        t("profile_layout.prompt_person_career", { name }),
        t("profile_layout.prompt_person_books", { name }),
        t("profile_layout.prompt_person_calendar", { name }),
        t("profile_layout.prompt_person_results", { name }),
        t("profile_layout.prompt_person_animal", { name }),
      ];

    case "candidate_profile":
      return [
        t("profile_layout.prompt_candidate_culture", { name }),
        t("profile_layout.prompt_candidate_interview", { name }),
        t("profile_layout.prompt_candidate_offer", { name }),
        t("profile_layout.prompt_candidate_avoid", { name }),
        t("profile_layout.prompt_candidate_learn", { name }),
      ];

    default:
      return [];
  }
}

/**
 * Hook to build standardized props for ProfileLayout component
 *
 * @example
 * ```tsx
 * const { layoutProps, tagsData, scope, isReady } = useProfileLayoutProps({
 *   contextType: "team_member_profile",
 *   entity: member,
 *   dimensionsData,
 *   surveyConfig,
 *   people: [member],
 *   loadingStates: [loadingTeamData, loadingDimensions, loadingPerson],
 *   additionalScope: { team_id: teamId },
 * });
 *
 * return (
 *   <ProfileLayout {...layoutProps}>
 *     {children}
 *   </ProfileLayout>
 * );
 * ```
 */
export function useProfileLayoutProps(
  config: ProfileLayoutConfig
): ProfileLayoutPropsResult {
  const { t } = useTranslation();

  const {
    contextType,
    entity,
    dimensionsData,
    surveyConfig,
    people,
    attachments = [],
    loadingStates,
    additionalScope = {},
    customExamplePrompts,
  } = config;

  // Build scope for AI chat
  const scope = useMemo<WisgptContextTokenQueryVariables>(() => {
    const baseScope: WisgptContextTokenQueryVariables = {
      survey_config: surveyConfig,
      ...additionalScope,
    };

    // Add person_id for person-based profiles
    if (contextType !== "team_profile" && entity.id) {
      baseScope.person_id = entity.id;
    }

    return baseScope;
  }, [contextType, entity.id, surveyConfig, additionalScope]);

  // Build tags data
  const tagsData = useMemo<CustomTagsData>(
    () => buildTagsData(dimensionsData, people, attachments, surveyConfig),
    [dimensionsData, people, attachments, surveyConfig]
  );

  // Determine if ready
  const isReady = useMemo(() => {
    const hasRequiredData = !!entity.id;
    const notLoading = !loadingStates.some(Boolean);
    return hasRequiredData && notLoading;
  }, [entity.id, loadingStates]);

  // Get greeting message
  const greetingMessage = useMemo(() => {
    const greetingKey = GREETING_KEYS[contextType];
    return t(greetingKey, { firstName: entity.first_name || "" });
  }, [contextType, entity.first_name, t]);

  // Build focused context
  const focusedContext = useMemo(
    () => buildFocusedContext(entity.id, greetingMessage),
    [entity.id, greetingMessage]
  );

  // Get example prompts
  const examplePrompts = useMemo(() => {
    if (customExamplePrompts && customExamplePrompts.length > 0) {
      return customExamplePrompts;
    }
    return getDefaultExamplePrompts(
      contextType,
      entity.first_name || entity.name || "",
      t
    );
  }, [contextType, entity.first_name, entity.name, customExamplePrompts, t]);

  // Build layout props
  const layoutProps = useMemo<Omit<GatewayChatProps, "chatTitle">>(() => {
    return {
      scope,
      focusedContext,
      flowId: getGptFlowKey(FLOW_ID_MAP[contextType] as GPTFlowKey),
      isReady,
      examplePrompts,
      messageTagsData: tagsData,
    };
  }, [scope, focusedContext, contextType, isReady, examplePrompts, tagsData]);

  const loadingMessage = t(LOADING_MESSAGE_KEYS[contextType]);

  return {
    layoutProps,
    tagsData,
    scope,
    isReady,
    loadingMessage,
  };
}

export default useProfileLayoutProps;
